import React, { useEffect, useState } from 'react';
import { fmtDate } from './PaymentsPanel';
import api from '../api/axios';
import ServiceLinesEditor, { ServiceLine, serviceLinePayload } from './ServiceLinesEditor';
import { DocLangSelect, DocLangHint } from './DocLang';
import { usePhrases, PhraseField, I18nText, Phrase, phraseI18n, phraseText, i18nText, sameI18n } from './DocumentPhrases';

// Bajarilgan ishlar dalolatnomalari - xizmat shartnomasi ichida (buxgalteriya
// sahifasi va servis case sahifasi). Backend: app/routers/service_acts.py -
// alohida "service_acts" moduli; servis muhandisi faqat qoralama; berish/
// imzolash/bekor qilish - can_approve (buxgalteriya / servis boshlig'i).
// Qoralamada raqam yo'q; berilgan/imzolangan o'chirilmaydi - bekor qilinadi.
interface ChecklistItem { key?: string | null; text?: string | null; i18n?: I18nText | null; answer?: 'yes' | 'no' | null; }
interface WorkTypeItem extends I18nText { key?: string | null; }
interface ServiceAct {
  id: number; number: string | null; contract_id: number; act_date: string;
  status: 'draft' | 'issued' | 'signed' | 'cancelled'; signed_date?: string | null;
  work_types?: string[] | null; work_type_other?: string | null; work_type_items?: WorkTypeItem[] | null;
  checklist_enabled: boolean; checklist?: ChecklistItem[] | null;
  customer_position?: string | null; customer_representative?: string | null;
  performer_position?: string | null; performer_representative?: string | null;
  customer_position_i18n?: I18nText | null; performer_position_i18n?: I18nText | null;
  objections?: string | null; notes?: string | null; document_lang?: string | null;
  items: ServiceLine[]; total_amount: number; contract_total?: number; other_acts_total?: number;
  exceeds_contract: boolean; final_payment_due?: string | null;
  contract_end_date?: string | null; can_approve: boolean;
}

const STATUS: Record<string, { label: string; color: string }> = {
  draft: { label: 'Qoralama', color: 'bg-gray-100 text-gray-700' },
  issued: { label: 'Berilgan', color: 'bg-blue-100 text-blue-700' },
  signed: { label: 'Imzolangan', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Bekor qilingan', color: 'bg-red-100 text-red-700' },
};
// backend TRANSITIONS bilan bir xil (raqamlangan dalolatnoma qoralamaga qaytmaydi)
const TRANSITIONS: Record<string, string[]> = {
  draft: ['draft', 'issued', 'signed'], issued: ['issued', 'signed', 'cancelled'],
  signed: ['signed', 'issued', 'cancelled'], cancelled: ['cancelled'],
};
const actNo = (a: { number: string | null }) => a.number || 'QORALAMA';
const STANDARD_QUESTIONS: Record<string, string> = {
  all_functions: 'Barcha funksiyalar tekshirildi', workable: 'Uskuna ishga yaroqli', staff_trained: "Xodimlar tanishtirildi va o'qitildi",
};
const fmt = (n?: number | null) => (n ?? 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ServiceActsPanel: React.FC<{ contractId: number; canFull: boolean }> = ({ contractId, canFull }) => {
  const [acts, setActs] = useState<ServiceAct[]>([]);
  const [edit, setEdit] = useState<ServiceAct | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState('uz');
  const phrases = usePhrases();

  const load = async () => {
    try { const r = await api.get('/service-acts', { params: { contract_id: contractId } }); setActs(r.data); } catch { setActs([]); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [contractId]);

  const create = async () => {
    setError('');
    try { const r = await api.post(`/service-acts/from-contract/${contractId}`); await load(); setEdit(r.data); }
    catch (e: any) { setError(e.response?.data?.detail || 'Xato'); }
  };

  const savedStatus = edit ? acts.find(a => a.id === edit.id)?.status || 'draft' : 'draft';
  const locked = savedStatus === 'signed' || savedStatus === 'cancelled';

  const save = async () => {
    if (!edit) return;
    if (!edit.contract_end_date && edit.status !== savedStatus && (edit.status === 'issued' || edit.status === 'signed')
        && !window.confirm(`${END_DATE_MSG} Baribir davom etasizmi?`)) return;
    if (edit.items.some(l => !l.name.trim())) { setError("Har bir qatorda nom bo'lishi kerak"); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        act_date: edit.act_date, status: edit.status,
        signed_date: edit.status === 'signed' || edit.status === 'cancelled' ? (edit.signed_date || null) : null,
        // ish turlari: ma'lumotnomadan tanlanganlar (uz/ru/en nusxasi) + "boshqa" (erkin matn)
        work_type_items: workItems(edit),
        work_types: [...workItems(edit).map(w => w.key).filter((k): k is string => !!k),
                     ...((edit.work_types || []).includes('other') ? ['other'] : [])],
        work_type_other: edit.work_type_other || null,
        checklist_enabled: edit.checklist_enabled,
        checklist: (edit.checklist || []).filter(c => c.key || c.i18n || (c.text || '').trim())
          .map(c => ({ key: c.key || null, text: c.key || c.i18n ? null : c.text, i18n: c.i18n || null, answer: c.answer || null })),
        // lavozim: ro'yxatdan bo'lsa uz/ru/en nusxasi (+ hujjat tilidagi matn), aks holda erkin matn
        customer_position: (edit.customer_position_i18n ? i18nText(edit.customer_position_i18n, edit.document_lang) : edit.customer_position) || null,
        customer_position_i18n: edit.customer_position_i18n || null,
        customer_representative: edit.customer_representative || null,
        performer_position: (edit.performer_position_i18n ? i18nText(edit.performer_position_i18n, edit.document_lang) : edit.performer_position) || null,
        performer_position_i18n: edit.performer_position_i18n || null,
        performer_representative: edit.performer_representative || null,
        document_lang: edit.document_lang || 'uz',
        objections: edit.objections || null,
        notes: edit.notes || null,
        // imzolangan dalolatnoma qatorlari qulflangan - yuborilmaydi
        ...(locked ? {} : { items: edit.items.map(serviceLinePayload) }),
      };
      const r = await api.put(`/service-acts/${edit.id}`, payload);
      setEdit(null); await load();
      if (r.data.exceeds_contract) setError("Diqqat: dalolatnomalar yig'indisi shartnoma summasidan oshib ketdi (qoralama).");
    } catch (e: any) { setError(e.response?.data?.detail || 'Xato'); }
    finally { setSaving(false); }
  };

  const remove = async (id: number) => {
    if (!window.confirm("Dalolatnomani o'chirishni tasdiqlaysizmi?")) return;
    try { await api.delete(`/service-acts/${id}`); await load(); } catch (e: any) { setError(e.response?.data?.detail || 'Xato'); }
  };

  const END_DATE_MSG = "Shartnomaning amal qilish muddati (tugash sanasi) kiritilmagan - xizmat shartnomasi 10.1-bandida bo'sh chiziq chiqadi.";

  const download = async (a: ServiceAct, format: 'pdf' | 'docx', docLang?: string) => {
    if (!a.contract_end_date && !window.confirm(`${END_DATE_MSG} Baribir yuklab olasizmi?`)) return;
    const useLang = docLang || lang;
    const r = await api.get(`/service-acts/${a.id}/export/${format}`, { params: { lang: useLang }, responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const link = document.createElement('a');
    link.href = url; link.download = `${a.number || `qoralama-${a.id}`}_${useLang}.${format}`; link.click();
    URL.revokeObjectURL(url);
  };

  const setE = (patch: Partial<ServiceAct>) => setEdit(prev => (prev ? { ...prev, ...patch } : prev));

  // Ish turlari: yangi dalolatnomada work_type_items; eskisida (null) - kalitlar ma'lumotnomadagi
  // shu kalitli qatorlarga moslanadi (birinchi saqlashda nusxaga aylanadi).
  const workItems = (a: ServiceAct): WorkTypeItem[] => {
    if (a.work_type_items) return a.work_type_items;
    return (a.work_types || []).filter(k => k !== 'other')
      .map(k => phrases.find(p => p.kind === 'work_type' && p.key === k))
      .filter((p): p is Phrase => !!p).map(p => ({ key: p.key, ...phraseI18n(p) }));
  };
  const toggleWork = (p: Phrase, on: boolean) => {
    if (!edit) return;
    const cur = workItems(edit).filter(w => !sameI18n(w, phraseI18n(p)));
    setE({ work_type_items: on ? [...cur, { key: p.key, ...phraseI18n(p) }] : cur });
  };
  const checklistLabel = (c: ChecklistItem, docLang?: string | null) =>
    (c.i18n ? i18nText(c.i18n, docLang) : '') || (c.key ? STANDARD_QUESTIONS[c.key] || c.key : '');

  return (
    <div className="mt-4 border-t pt-4">
      <div className="flex justify-between items-center mb-2 gap-2 flex-wrap">
        <h4 className="font-semibold text-gray-700 text-sm">📑 Bajarilgan ishlar dalolatnomalari</h4>
        <div className="flex gap-2 items-center">
          <select value={lang} onChange={e => setLang(e.target.value)} className="border rounded px-2 py-1 text-xs">
            <option value="uz">UZ</option><option value="ru">RU</option><option value="en">EN</option>
          </select>
          {canFull && <button onClick={create} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700">+ Dalolatnoma</button>}
        </div>
      </div>
      {error && <div className="bg-red-50 text-red-600 p-2 rounded mb-2 text-xs">{error}</div>}
      {acts.length === 0 && <p className="text-xs text-gray-400">Dalolatnoma yo&apos;q. Qatorlar shartnoma Ilova №2 dan olinadi (qisman bajarilgan ish uchun tahrirlanadi).</p>}
      <div className="space-y-2">
        {acts.map(a => (
          <div key={a.id} className="border rounded-lg p-2 text-xs flex justify-between items-center gap-2 flex-wrap">
            <div>
              <span className="font-mono font-medium">{actNo(a)}</span> · {fmtDate(a.act_date)}
              <span className={`ml-2 px-2 py-0.5 rounded-full ${STATUS[a.status].color}`}>{STATUS[a.status].label}</span>
              <span className="ml-2 font-medium">{fmt(a.total_amount)}</span>
              {a.exceeds_contract && <span className="ml-2 text-red-600">⚠ shartnoma summasidan oshadi</span>}
              {a.final_payment_due && <span className="ml-2 text-gray-500">Yakuniy to&apos;lov muddati: {fmtDate(a.final_payment_due)} (ma&apos;lumot)</span>}
            </div>
            <div className="flex gap-1">
              <button onClick={() => download(a, 'pdf', a.document_lang || undefined)} title={`Hujjat tili: ${(a.document_lang || lang).toUpperCase()}`}
                className="px-2 py-1 border rounded hover:bg-gray-50">PDF {(a.document_lang || lang).toUpperCase()}</button>
              <button onClick={() => download(a, 'docx', a.document_lang || undefined)} className="px-2 py-1 border rounded hover:bg-gray-50">DOCX</button>
              {a.document_lang && a.document_lang !== lang && (
                <button onClick={() => download(a, 'pdf', lang)} title="Yuqorida tanlangan tilda" className="px-2 py-1 border rounded hover:bg-gray-50 text-gray-500">PDF {lang.toUpperCase()}</button>
              )}
              {canFull && (a.status === 'draft' || a.can_approve) && a.status !== 'cancelled' && (
                <button onClick={() => { setError(''); setEdit({ ...a, items: a.items.map(l => ({ ...l })), checklist: (a.checklist || []).map(c => ({ ...c })) }); }}
                  className="px-2 py-1 border rounded hover:bg-gray-50">✏️</button>
              )}
              {canFull && a.status === 'draft' && <button onClick={() => remove(a.id)} title="Qoralamani o'chirish" className="px-2 py-1 border rounded text-red-500 hover:bg-red-50">✕</button>}
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-2 md:p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl p-4 md:p-6 max-h-[95vh] overflow-y-auto text-sm">
            <h3 className="text-lg font-bold mb-3">{actNo(edit)}</h3>
            {error && <div className="bg-red-50 text-red-600 p-2 rounded mb-2 text-xs">{error}</div>}
            {!edit.contract_end_date && (edit.status === 'issued' || edit.status === 'signed') && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 p-2 rounded mb-2 text-xs">
                ⚠ {END_DATE_MSG} Dalolatnomani berish/imzolashdan oldin shartnomada tugash sanasini kiriting.
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Sana</label>
                <input type="date" value={edit.act_date} onChange={e => setE({ act_date: e.target.value })} className="w-full border rounded px-2 py-1" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Holat</label>
                <select value={edit.status} onChange={e => setE({ status: e.target.value as any })} className="w-full border rounded px-2 py-1"
                  disabled={!edit.can_approve}>
                  {(edit.can_approve ? TRANSITIONS[savedStatus] : [savedStatus]).map(k => <option key={k} value={k}>{STATUS[k].label}</option>)}
                </select>
                {!edit.can_approve && <p className="text-[11px] text-gray-400 mt-1">Berish/imzolash - buxgalteriya yoki servis bo&apos;limi boshlig&apos;i.</p>}
                {edit.can_approve && savedStatus === 'draft' && <p className="text-[11px] text-gray-400 mt-1">Raqam &quot;Berilgan&quot;ga o&apos;tganda beriladi.</p>}
                {savedStatus !== 'draft' && <p className="text-[11px] text-gray-400 mt-1">O&apos;chirib bo&apos;lmaydi - kerak bo&apos;lsa &quot;Bekor qilingan&quot; (raqam saqlanadi).</p>}
              </div>
              {edit.status === 'signed' && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Imzolangan sana</label>
                  <input type="date" value={edit.signed_date || ''} onChange={e => setE({ signed_date: e.target.value })} className="w-full border rounded px-2 py-1" />
                  <p className="text-[11px] text-gray-400 mt-1">Yakuniy to&apos;lov muddati shu sanadan (ish kunlari) hisoblanadi.</p>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-1">
              <DocLangSelect value={edit.document_lang || 'uz'} onChange={v => setE({ document_lang: v })} />
              <div className="md:col-span-3 self-end"><DocLangHint lang={edit.document_lang} /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
              <PhraseField label="Buyurtmachi vakili - lavozimi" kind="position" phrases={phrases} lang={edit.document_lang}
                text={edit.customer_position} i18n={edit.customer_position_i18n} placeholder="Lavozim (hujjat tilida)"
                onChange={(text, i18n) => setE({ customer_position: text, customer_position_i18n: i18n })} />
              <div>
                <label className="block text-xs text-gray-600 mb-1">Buyurtmachi vakili - F.I.Sh.</label>
                <input value={edit.customer_representative || ''} onChange={e => setE({ customer_representative: e.target.value })}
                  placeholder="Familiya I.O." className="w-full border rounded px-2 py-1" />
              </div>
              <PhraseField label="Pudratchi vakili - lavozimi" kind="position" phrases={phrases} lang={edit.document_lang}
                text={edit.performer_position} i18n={edit.performer_position_i18n} placeholder="Lavozim (hujjat tilida)"
                onChange={(text, i18n) => setE({ performer_position: text, performer_position_i18n: i18n })} />
              <div>
                <label className="block text-xs text-gray-600 mb-1">Pudratchi vakili - F.I.Sh.</label>
                <input value={edit.performer_representative || ''} onChange={e => setE({ performer_representative: e.target.value })}
                  placeholder="bo'sh - sozlamadagi imzolovchi" className="w-full border rounded px-2 py-1" />
              </div>
              <p className="md:col-span-4 text-[11px] text-gray-400 -mt-2">
                Imzo ostida matndagi F.I.Sh. chiqadi (bir odam). Pudratchi F.I.Sh. bo&apos;sh bo&apos;lsa yoki imzolovchining o&apos;zi bo&apos;lsa -
                shartnomadagi kabi har tilda sozlamadagi imzolovchi shakli. Lavozim ro&apos;yxatdan tanlansa - har til o&apos;z variantini oladi.
              </p>
            </div>
            <div className="mb-3">
              <label className="block text-xs text-gray-600 mb-1">Ish turi (bir nechtasi)</label>
              <div className="flex flex-wrap gap-3">
                {phrases.filter(p => p.kind === 'work_type').map(p => (
                  <label key={p.id} className="flex items-center gap-1 text-xs" title={`${p.text_uz} / ${p.text_ru} / ${p.text_en}`}>
                    <input type="checkbox" checked={workItems(edit).some(w => sameI18n(w, phraseI18n(p)))}
                      onChange={e => toggleWork(p, e.target.checked)} />
                    {phraseText(p, edit.document_lang)}
                  </label>
                ))}
                {workItems(edit).filter(w => !phrases.some(p => p.kind === 'work_type' && sameI18n(w, phraseI18n(p)))).map((w, i) => (
                  <label key={`snap-${i}`} className="flex items-center gap-1 text-xs text-gray-500" title="Ma'lumotnomada o'zgargan/o'chirilgan - saqlangan nusxa">
                    <input type="checkbox" checked onChange={() => setE({ work_type_items: workItems(edit).filter(x => x !== w) })} />
                    {i18nText(w, edit.document_lang)}
                  </label>
                ))}
                <label className="flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={(edit.work_types || []).includes('other')}
                    onChange={e => setE({ work_type_items: workItems(edit),
                      work_types: e.target.checked ? [...(edit.work_types || []), 'other'] : (edit.work_types || []).filter(x => x !== 'other') })} />
                  Boshqa (erkin matn)
                </label>
              </div>
              {(edit.work_types || []).includes('other') && (
                <>
                  <input value={edit.work_type_other || ''} onChange={e => setE({ work_type_other: e.target.value })}
                    placeholder="Boshqa ish turi (erkin matn)" className="mt-1 w-full md:w-1/2 border rounded px-2 py-1 text-xs" />
                  <DocLangHint lang={edit.document_lang} />
                </>
              )}
            </div>
            <div className="mb-3 border rounded p-2">
              <label className="flex items-center gap-2 text-xs font-medium">
                <input type="checkbox" checked={edit.checklist_enabled} onChange={e => setE({ checklist_enabled: e.target.checked })} />
                Ha/Yo&apos;q tekshiruv bloki (o&apos;chiq bo&apos;lsa PDF&apos;da chiqmaydi)
              </label>
              {edit.checklist_enabled && (
                <div className="mt-2 space-y-1">
                  {(edit.checklist || []).map((c, i) => (
                    <div key={i} className="flex gap-2 items-center text-xs">
                      {c.key || c.i18n ? <span className="flex-1">{checklistLabel(c, edit.document_lang)}</span> : (
                        <input value={c.text || ''} onChange={e => setE({ checklist: (edit.checklist || []).map((x, j) => j === i ? { ...x, text: e.target.value } : x) })}
                          placeholder="Savol" className="flex-1 border rounded px-2 py-1" />
                      )}
                      <select value={c.answer || ''} onChange={e => setE({ checklist: (edit.checklist || []).map((x, j) => j === i ? { ...x, answer: (e.target.value || null) as any } : x) })}
                        className="border rounded px-2 py-1">
                        <option value="">—</option><option value="yes">Ha</option><option value="no">Yo&apos;q</option>
                      </select>
                      <button type="button" onClick={() => setE({ checklist: (edit.checklist || []).filter((_, j) => j !== i) })} className="text-red-500 px-1">✕</button>
                    </div>
                  ))}
                  <div className="flex gap-2 flex-wrap items-center">
                    <select value="" onChange={e => {
                      const p = phrases.find(x => String(x.id) === e.target.value);
                      if (p) setE({ checklist: [...(edit.checklist || []), { key: null, text: null, i18n: phraseI18n(p), answer: null }] });
                    }} className="text-xs border rounded px-2 py-1">
                      <option value="">+ Ro&apos;yxatdan qator…</option>
                      {phrases.filter(p => p.kind === 'checklist').map(p => <option key={p.id} value={p.id}>{phraseText(p, edit.document_lang)}</option>)}
                    </select>
                    <button type="button" onClick={() => setE({ checklist: [...(edit.checklist || []), { key: null, text: '', answer: null }] })}
                      className="text-xs text-blue-600 px-2 py-1 border border-dashed rounded">+ Erkin savol</button>
                  </div>
                  {(edit.checklist || []).some(c => !c.key && !c.i18n) && <DocLangHint lang={edit.document_lang} />}
                </div>
              )}
            </div>
            <div className="mb-3">
              <p className="text-xs font-medium text-gray-600 mb-1">Bajarilgan ishlar (standart - shartnoma Ilova №2; qisman bajarilgan bo&apos;lsa tahrirlang)</p>
              {locked && <p className="text-[11px] text-orange-600 mb-1">Imzolangan - qatorlar qulflangan. O&apos;zgartirish uchun holatni &quot;Berilgan&quot;ga qaytaring.</p>}
              <ServiceLinesEditor lines={edit.items} onChange={items => setE({ items })} catalog={[]} instruments={null}
                instrumentsHint="Uskunalar shartnomadan keladi; kerak bo'lsa erkin matn." disabled={locked} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Buyurtmachining e&apos;tirozlari</label>
                <textarea value={edit.objections || ''} onChange={e => setE({ objections: e.target.value })} rows={3} className="w-full border rounded px-2 py-1" />
                <p className="text-[11px] text-gray-400 mt-1">Bo&apos;sh bo&apos;lsa hujjatda &quot;Buyurtmachining e&apos;tirozlari yo&apos;q&quot; jumlasi chiqadi; to&apos;ldirilsa - jumla o&apos;rniga shu matn.</p>
                <DocLangHint lang={edit.document_lang} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Izohlar</label>
                <textarea value={edit.notes || ''} onChange={e => setE({ notes: e.target.value })} rows={3} className="w-full border rounded px-2 py-1" />
                <p className="text-[11px] text-gray-400 mt-1">Qo&apos;shimcha ma&apos;lumot (masalan sarf materiallari). Jumlaga ta&apos;sir qilmaydi.</p>
                <DocLangHint lang={edit.document_lang} />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setEdit(null)} className="px-4 py-2 border rounded-lg">Bekor qilish</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">
                {saving ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceActsPanel;
